import { ReactElement, useEffect, useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'app/components/ui/dialog'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Checkbox } from 'app/components/ui/checkbox'
import { gqlFetchList, gqlQuery } from 'app/lib/graphqlClient'
import { opsClient } from 'app/lib/opsUrqlClient'
import { graphql } from 'app/generated/gql'
import { InternalOnlySwitch } from './InternalOnlySwitch'
import { logError } from 'lib/logging'
import { buildTargetSearch, type TargetRef } from './featureToggleModel'

const OPS_USERS_QUERY = graphql(`
  query AddOverrideOpsUsers($search: String) {
    opsUsers(search: $search) {
      hits {
        id
        name
        email
      }
    }
  }
`)

export type AddOverrideModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  kind: TargetRef[`kind`]
  existingIds: Set<string>
  onConfirm: (ids: string[]) => void | Promise<void>
  title?: string
  confirmLabel?: string
}

export function AddOverrideModal({
  open,
  onOpenChange,
  kind,
  existingIds,
  onConfirm,
  title,
  confirmLabel = `Add`,
}: AddOverrideModalProps): ReactElement {
  const entity = kind === `Workspace` ? `workspaces` : `users`
  // Users carry an email to disambiguate similar names; workspaces don't.
  const fields = kind === `User` ? `id name email` : `id name`
  const [query, setQuery] = useState(``)
  const [options, setOptions] = useState<Array<{ id: string; name: string; email?: string }>>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)
  const [internalOnly, setInternalOnly] = useState(false)

  useEffect(() => {
    if (!open) {
      setQuery(``)
      setSelected(new Set())
      setInternalOnly(false)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    // Debounce keystrokes (250ms) to match the other search inputs in the app.
    const timer = setTimeout(() => {
      // The user picker uses the super-only ops `opsUsers` query so a super can grant a
      // toggle override to any user across all orgs and workspaces, including org and super
      // users the scoped `users` list hides (TRK-6058). Workspaces use the standard list.
      const request =
        kind === `User`
          ? gqlQuery(
              OPS_USERS_QUERY,
              { search: buildTargetSearch(kind, query, internalOnly) },
              { client: opsClient },
            ).then((res) => res.data?.opsUsers.hits ?? [])
          : gqlFetchList<{ id: string; name: string; email?: string }>(
              `workspaces`,
              { search: buildTargetSearch(kind, query, internalOnly), pagination: { limit: 50 } },
              fields,
            ).then((res) => res.hits ?? [])
      request
        .then((hits) => {
          if (!cancelled) setOptions(hits)
        })
        .catch((error) => {
          // Without this the list just reads "No {entity} found" for a failed request.
          logError(`AddOverrideModal ${entity} search failed`, error)
          if (!cancelled) setOptions([])
        })
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, entity, kind, query, fields, internalOnly])

  const available = useMemo(() => options.filter((o) => !existingIds.has(o.id)), [options, existingIds])

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const handleConfirm = async () => {
    setSaving(true)
    await onConfirm([...selected])
    setSaving(false)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title ?? `Add ${kind.toLowerCase()} override${selected.size > 1 ? `s` : ``}`}</DialogTitle>
        </DialogHeader>
        <Input placeholder={`Search ${entity}`} value={query} onChange={(e) => setQuery(e.target.value)} />
        {kind === `User` && <InternalOnlySwitch checked={internalOnly} onCheckedChange={setInternalOnly} />}
        <div className="max-h-72 divide-y divide-subtle overflow-y-auto rounded-md border border-default">
          {available.length === 0 ? (
            <p className="px-3 py-4 text-center text-sm text-minimal">No {entity} found</p>
          ) : (
            available.map((o) => (
              <label
                key={o.id}
                className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-surface-subtle"
              >
                <Checkbox checked={selected.has(o.id)} onCheckedChange={() => toggle(o.id)} />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{o.name}</span>
                  {o.email && <span className="truncate text-xs text-minimal">{o.email}</span>}
                </span>
              </label>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="tertiary" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="secondary" size="sm" disabled={selected.size === 0 || saving} onClick={handleConfirm}>
            {confirmLabel} {selected.size || ``}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
